import { ImageItem } from "@/types/minds-eye";

interface SlideshowOptions {
  width: number;
  height: number;
  durationPerSlide: number; // seconds
  fadeDuration: number; // seconds for crossfade
  loops: number;
  audioBlob?: Blob | null;
}

const DEFAULT_OPTIONS: SlideshowOptions = {
  width: 1280,
  height: 720,
  durationPerSlide: 6,
  fadeDuration: 1,
  loops: 1,
};

type SlideSource =
  | { kind: "image"; el: HTMLImageElement }
  | { kind: "video"; el: HTMLVideoElement };

const VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".m4v"];

function isVideoUrl(url: string): boolean {
  const lower = url.split("?")[0].toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Loads an image from a URL and returns an HTMLImageElement.
 */
const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

/**
 * Loads a video from a URL, seeks to 0.5s, and returns the HTMLVideoElement ready to draw.
 */
const loadVideo = (url: string): Promise<HTMLVideoElement> =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = url;

    const onReady = () => {
      video.removeEventListener("seeked", onReady);
      resolve(video);
    };

    video.addEventListener("loadeddata", () => {
      // Seek to 0.5s to get a meaningful frame (avoids black first-frame)
      video.currentTime = Math.min(0.5, video.duration || 0);
      video.addEventListener("seeked", onReady);
    });

    video.addEventListener("error", () =>
      reject(new Error(`Failed to load video: ${url}`))
    );

    video.load();
  });

/**
 * Gets width/height of a slide source.
 */
function getSourceDimensions(src: SlideSource): { w: number; h: number } {
  if (src.kind === "image") {
    return { w: src.el.width, h: src.el.height };
  }
  return { w: src.el.videoWidth || 1280, h: src.el.videoHeight || 720 };
}

/**
 * Draws a slide source (image or video frame) to fill the canvas (cover mode)
 * with a dark overlay, then renders the gratitude text on top.
 */
function drawSlide(
  ctx: CanvasRenderingContext2D,
  source: SlideSource,
  text: string,
  width: number,
  height: number,
  opacity: number
) {
  ctx.save();
  ctx.globalAlpha = opacity;

  const { w: srcW, h: srcH } = getSourceDimensions(source);
  const imgRatio = srcW / srcH;
  const canvasRatio = width / height;
  let drawW: number, drawH: number, drawX: number, drawY: number;

  if (imgRatio > canvasRatio) {
    drawH = height;
    drawW = height * imgRatio;
    drawX = (width - drawW) / 2;
    drawY = 0;
  } else {
    drawW = width;
    drawH = width / imgRatio;
    drawX = 0;
    drawY = (height - drawH) / 2;
  }

  ctx.drawImage(source.el, drawX, drawY, drawW, drawH);

  // Soft gradient overlay at bottom for text readability (gentler than before)
  const gradient = ctx.createLinearGradient(0, height * 0.55, 0, height);
  gradient.addColorStop(0, "rgba(0, 0, 0, 0)");
  gradient.addColorStop(0.5, "rgba(0, 0, 0, 0.35)");
  gradient.addColorStop(1, "rgba(0, 0, 0, 0.7)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // Draw gratitude text — refined typography
  if (text) {
    const maxWidth = width * 0.72;
    // Smaller, more elegant sizing (~28px at 1280w vs previous ~41px)
    const fontSize = Math.round(width * 0.022);
    ctx.font = `400 italic ${fontSize}px "Georgia", "Times New Roman", serif`;
    ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";

    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const lineHeight = fontSize * 1.45;
    const totalTextHeight = lines.length * lineHeight;
    const bottomPadding = Math.round(height * 0.08);
    const startY = height - bottomPadding - totalTextHeight + lineHeight;

    ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
    ctx.shadowBlur = 12;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;

    lines.forEach((line, i) => {
      ctx.fillText(line, width / 2, startY + i * lineHeight);
    });
  }


  ctx.restore();
}

/**
 * Generates a slideshow video from images/videos with gratitude text overlay.
 * Returns a Blob for the resulting video.
 */
export async function generateSlideshowVideo(
  images: ImageItem[],
  options?: Partial<SlideshowOptions>,
  onProgress?: (progress: number) => void
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { width, height, durationPerSlide, fadeDuration, loops = 1, audioBlob } = opts;

  // Load all media (images and videos)
  const loadedSources: SlideSource[] = [];
  for (let i = 0; i < images.length; i++) {
    const url = images[i].url;
    const itemType = images[i].type || "";
    const isVideo = itemType === "video" || isVideoUrl(url);

    try {
      if (isVideo) {
        const vid = await loadVideo(url);
        loadedSources.push({ kind: "video", el: vid });
      } else {
        const img = await loadImage(url);
        loadedSources.push({ kind: "image", el: img });
      }
    } catch {
      // Create a fallback colored canvas for failed media
      const fallback = document.createElement("canvas");
      fallback.width = width;
      fallback.height = height;
      const fCtx = fallback.getContext("2d")!;
      fCtx.fillStyle = "#1a1030";
      fCtx.fillRect(0, 0, width, height);
      const fbImg = new Image();
      fbImg.src = fallback.toDataURL();
      await new Promise<void>((r) => { fbImg.onload = () => r(); });
      loadedSources.push({ kind: "image", el: fbImg });
    }
    onProgress?.((i + 1) / (images.length + 1) * 30);
  }

  // Set up canvas
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  // Set up MediaRecorder
  const stream = canvas.captureStream(30);

  // Mix in audio track if provided
  let audioCtx: AudioContext | null = null;
  let audioSource: AudioBufferSourceNode | null = null;
  if (audioBlob && audioBlob.size > 0) {
    try {
      audioCtx = new AudioContext();
      const arrayBuf = await audioBlob.arrayBuffer();
      console.log("Audio blob size:", audioBlob.size, "type:", audioBlob.type);
      
      let audioBuffer: AudioBuffer;
      try {
        audioBuffer = await audioCtx.decodeAudioData(arrayBuf.slice(0));
      } catch (decodeErr) {
        console.warn("Direct decode failed, trying via Audio element fallback:", decodeErr);
        const audioUrl = URL.createObjectURL(audioBlob);
        const audioEl = new Audio(audioUrl);
        await new Promise<void>((resolve, reject) => {
          audioEl.oncanplaythrough = () => resolve();
          audioEl.onerror = () => reject(new Error("Audio element failed to load"));
          audioEl.load();
        });
        URL.revokeObjectURL(audioUrl);
        const offlineCtx = new OfflineAudioContext(2, audioCtx.sampleRate * 30, audioCtx.sampleRate);
        const arrayBuf2 = await audioBlob.arrayBuffer();
        audioBuffer = await offlineCtx.decodeAudioData(arrayBuf2);
      }
      
      audioSource = audioCtx.createBufferSource();
      audioSource.buffer = audioBuffer;
      audioSource.loop = true;
      const dest = audioCtx.createMediaStreamDestination();
      audioSource.connect(dest);
      audioSource.start();
      dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
      console.log("Audio track added to video stream successfully");
    } catch (err) {
      console.warn("Could not add audio track to video:", err);
    }
  }

  const preferredTypes = [
    "video/mp4;codecs=avc1",
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm",
  ];
  const mimeType = preferredTypes.find((t) => MediaRecorder.isTypeSupported(t)) || "video/webm";
  const mediaRecorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: 4_000_000,
  });

  const chunks: Blob[] = [];
  mediaRecorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  const recordingDone = new Promise<Blob>((resolve) => {
    mediaRecorder.onstop = () => {
      resolve(new Blob(chunks, { type: mimeType }));
    };
  });

  mediaRecorder.start(100);

  // Render each slide, timed to wall-clock so slide length matches MediaRecorder's real-time capture.
  // (A frame-count loop is unreliable: drawImage on a playing video can take far longer than 1/fps,
  // stretching a 6s slide to 60s+ of recorded output.)
  const totalSlides = loadedSources.length;
  const slideDurationMs = durationPerSlide * 1000;
  const fadeDurationMs = fadeDuration * 1000;
  const totalDurationMs = totalSlides * slideDurationMs * loops;
  let elapsedTotalMs = 0;

  const nextFrame = () =>
    new Promise<void>((r) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => r());
      } else {
        setTimeout(r, 16);
      }
    });

  for (let loop = 0; loop < loops; loop++) {
    for (let slideIdx = 0; slideIdx < totalSlides; slideIdx++) {
      const source = loadedSources[slideIdx];
      const text = images[slideIdx].gratitudeStatement || "";

      // For video slides, start playback from the beginning
      if (source.kind === "video") {
        source.el.currentTime = 0;
        try { await source.el.play(); } catch {}
      }

      const slideStart = performance.now();
      let now = slideStart;
      while (now - slideStart < slideDurationMs) {
        const elapsed = now - slideStart;
        let opacity = 1;
        if (elapsed < fadeDurationMs) {
          opacity = elapsed / fadeDurationMs;
        } else if (elapsed > slideDurationMs - fadeDurationMs) {
          opacity = Math.max(0, (slideDurationMs - elapsed) / fadeDurationMs);
        }

        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, width, height);
        drawSlide(ctx, source, text, width, height, opacity);

        await nextFrame();
        now = performance.now();
      }

      // Pause video after its slide is done
      if (source.kind === "video") {
        source.el.pause();
      }

      elapsedTotalMs += slideDurationMs;
      onProgress?.(30 + (elapsedTotalMs / totalDurationMs) * 65);
    }
  }

  mediaRecorder.stop();
  
  // Clean up resources
  if (audioSource) {
    try { audioSource.stop(); } catch {}
  }
  if (audioCtx) {
    try { audioCtx.close(); } catch {}
  }
  // Clean up video elements
  for (const src of loadedSources) {
    if (src.kind === "video") {
      src.el.pause();
      src.el.src = "";
      src.el.load();
    }
  }
  
  onProgress?.(98);

  const blob = await recordingDone;
  onProgress?.(100);
  return blob;
}
